import { formatDate, formatNumber, formatPercent } from '@angular/common';
import {
    CmpsScorecardPillarInformationData,
    CmpsScorecardSubHeaderData,
} from '@cmps/ng-kit';
import { TranslateService } from '@ngx-translate/core';

import { HeaderRowData } from '../card--my-coaching-card/models/header-row-data.model';
import { I18nService } from '../i18n/i18n.service';
import { OverallScoreGridOptions } from '../scorecard/core/overall-score-grid-options.model';
import { WidgetType } from '../scorecard/core/template-definition.model';
import { TrendType } from '../scorecard/core/trend-type.model';
import { DescriptionType } from '../scorecard/scorecard-data/description-type.model';
import { Scorecard } from '../scorecard/scorecard-next.model';
import {
    PeriodTypeShallow,
    ScorecardDataDto,
    ScorecardDto,
} from '../scorecard/scorecard.model';
import { epicDependencies } from '../store';
import { User } from '../user/user.model';

import { defaultColor } from './constant/sc-category-color-constant';
import { getTrendData } from './general/get-trend-data';
import { TranslationKeyMap } from './my-scorecard.i18n';
import { MenuItems, PillarData, ScorecardData } from './my-scorecard.model';

export function getScoreData(scorecard: Scorecard, user: User): ScorecardData {
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);
    const i18nService = epicDependencies.injector.get<I18nService>(I18nService);
    const userLocale = i18nService.locale;
    const sc: ScorecardData = {};
    sc.headerRowData = getHeaderRowData(scorecard.header, user, userLocale);
    sc.scorecardSubHeader = {
        supergroupLabel: translateService.instant(
            TranslationKeyMap._804_SUPERGROUP_,
        ),
        scorecardId: translateService.instant(
            TranslationKeyMap._9947_SCORECARD_ID_,
        ),
        supergroupValue: scorecard.header.groupName,
        scorecardValue: scorecard.header.scorecardId.toString(),
    };

    const adjustedSc = scorecard.data.overallScores?.find(
        (e) => e.descriptionTypeId === DescriptionType.ADJUSTED_SCORE,
    );
    const overallScores = scorecard.data.overallScores?.find(
        (e) => e.descriptionTypeId === DescriptionType.OVERALL_SCORE,
    );
    const adjustedScore = adjustedSc != null ? adjustedSc : overallScores;
    const originalScore = scorecard.data.overallScores?.find(
        (e) => e.descriptionTypeId === DescriptionType.ORIGINAL_SCORE,
    );

    sc.adjustScoreData = {
        scoreLabel: translateService.instant(
            TranslationKeyMap._9361_ADJUSTED_SCORE_,
        ),
        scoreValue:
            adjustedScore?.scoreValue == null
                ? '-'
                : formatNumber(adjustedScore.scoreValue, userLocale, '1.0-2'),
    };

    const trend =
        (originalScore?.scoreValue != null ||
            scorecard.header.prevAdjustedPoints) ===
        scorecard.header.adjustedPoints
            ? TrendType.NEUTRAL
            : scorecard.header.trendId;

    if (originalScore != null) {
        sc.originalScoreData = {
            scoreLabel: translateService.instant(
                TranslationKeyMap._9360_ORIGINAL_SCORE_,
            ),
            scoreValue: formatNumber(
                originalScore.scoreValue,
                userLocale,
                '1.0-2',
            ),
        };
    } else {
        sc.adjustScoreData.scoreLabel = translateService.instant(
            TranslationKeyMap._752_SCORE,
        );
    }

    sc.trendData = getTrendData(
        adjustedScore,
        scorecard.header.prevAdjustedPoints,
        trend,
    );
    let dropdownMenuData = {
        dropdownMenuValues: [
            {
                value: translateService.instant(
                    TranslationKeyMap._9946_LAUNCH_SCORECARDS,
                ),
                selected: false,
                id: MenuItems.LAUNCH_SCORECARD,
            },
            {
                value: translateService.instant(TranslationKeyMap._989_TREND),
                selected: false,
                id: MenuItems.TREND,
            },
            {
                value: translateService.instant(
                    TranslationKeyMap._9948_ADD_RANK,
                ),
                selected: false,
                id: MenuItems.ADD_RANK,
            },
            {
                value: translateService.instant(
                    TranslationKeyMap._9949_ADD_BONUS,
                ),
                selected: false,
                id: MenuItems.ADD_BONUS,
            },
        ],
    };
    sc.dropdownMenuData = dropdownMenuData;
    const rankWidget =
        scorecard?.data?.widgetTemplateConfig != null
            ? scorecard.data.widgetTemplateConfig.widgets.find(
                  (e) => e.widgetType === WidgetType.RANKINGS_GRID && e.enabled,
              )
            : null;
    const bonusWidget =
        scorecard?.data?.widgetTemplateConfig != null
            ? scorecard.data.widgetTemplateConfig.widgets.find(
                  (e) =>
                      e.widgetType === WidgetType.COMPENSATION_BONUS &&
                      e.enabled,
              )
            : null;
    const scCategory =
        scorecard?.data?.widgetTemplateConfig != null
            ? scorecard.data.widgetTemplateConfig.widgets.find(
                  (e) =>
                      e.widgetType === WidgetType.OVERALL_SCORE_GRID &&
                      e.enabled,
              )
            : null;
    const scOsConfigOptions = scorecard?.data?.templateConfig?.widgets.find(
        (e) => e.widgetType === WidgetType.OVERALL_SCORE_GRID,
    ).options as OverallScoreGridOptions;

    if (rankWidget != null) {
        sc.rankData = {
            isVisible: false,
            titleLabel: rankWidget.name,
            data: scorecard.data?.ranks?.map((e) => {
                const value =
                    e?.rankResultScorecardPeriod !== '' &&
                    e?.totalPositionsScorecardPeriod;
                return {
                    titleName: e.rankName != null ? e.rankName : '',
                    value:
                        value != null
                            ? `${e?.rankResultScorecardPeriod}/${e?.totalPositionsScorecardPeriod}`
                            : '-',
                };
            }),
        };
    } else {
        dropdownMenuData = {
            dropdownMenuValues: dropdownMenuData.dropdownMenuValues.filter(
                (e) => e.id !== MenuItems.ADD_RANK,
            ),
        };
    }

    if (bonusWidget != null) {
        sc.bonusData = {
            isVisible: false,
            titleLabel: bonusWidget.name,
            data: [
                {
                    titleName: translateService.instant(
                        TranslationKeyMap._9308_ESTIMATED_PAYOUT,
                    ),
                    value: scorecard.data.compensationBonus.bonusAmount.toString(),
                },
            ],
        };
    } else {
        dropdownMenuData = {
            dropdownMenuValues: dropdownMenuData.dropdownMenuValues.filter(
                (e) => e.id !== MenuItems.ADD_BONUS,
            ),
        };
    }

    if (scCategory != null && scOsConfigOptions?.showCategory === true) {
        sc.scCategory = {
            color: scorecard.header.performanceClass?.colour ?? defaultColor,
            value:
                scorecard.header.performanceClass
                    ?.scorecardPerformanceClassName ??
                translateService.instant(TranslationKeyMap._9402_NA),
            label: translateService.instant(
                TranslationKeyMap._9880_MI2_SCORECARD_CATEGORY,
            ),
        };
    }
    sc.dropdownMenuData = dropdownMenuData;
    sc.scorecardPillarInformationData = getPillars(scorecard.data);
    return sc;
}
export function getHeaderRowData(
    scorecardData: ScorecardDto,
    user: User,
    userLocale: string,
): HeaderRowData {
    const startDateFormatted = formatDate(
        scorecardData.startDate,
        'MMMM d, YYYY',
        userLocale,
    );
    const endDateFormatted = formatDate(
        scorecardData.endDate,
        'MMMM d, YYYY',
        userLocale,
    );
    return {
        titleLabel: `${user.firstName} ${user.lastName}`,
        periodDate: `${startDateFormatted} - ${endDateFormatted}`,
        hasHeaderIcon: true,
    };
}
export function getPillars(
    sd: ScorecardDataDto,
): CmpsScorecardPillarInformationData[] {
    if (sd.pillarsAndMetrics == null) {
        return [];
    }
    const i18nService = epicDependencies.injector.get<I18nService>(I18nService);
    const userLocale = i18nService.locale;

    const pillarsData: CmpsScorecardPillarInformationData[] = [];
    let pillarScores: PillarData[] = [];
    const pillarsIds = new Set(sd.pillarsAndMetrics.map((e) => e.pillarId));
    pillarsIds.forEach((e) => {
        const value = sd.pillarsAndMetrics
            .filter((item) => item.pillarId === e)
            .reduce((a, b) => a + b.adjustedPoints * b.weight, 0);
        const displayOrder = sd.templateConfig?.pillars?.find(
            (p) => Number(p.identifier) === e,
        ).displayOrder;
        pillarScores.push({ pillarId: e, pillarValue: value, displayOrder });
    });
    const totalScorecardValue = pillarScores.reduce(
        (a, b) => a + b.pillarValue,
        0,
    );
    const pillarColors = getPillarColorsArr(pillarScores.length);

    pillarScores = sort(pillarScores, 'displayOrder');

    pillarScores.forEach((e, i) => {
        const pillarName = sd.templateConfig?.pillars?.find(
            (pillar) => e.pillarId.toString() === pillar.identifier,
        );

        pillarsData.push({
            labelValue: formatPercent(
                !isNaN(e.pillarValue / totalScorecardValue)
                    ? e.pillarValue / totalScorecardValue
                    : 0,
                userLocale,
                '1.0-0',
            ),
            percent: `${
                !isNaN(e.pillarValue / totalScorecardValue)
                    ? (e.pillarValue / totalScorecardValue) * 100
                    : 0
            }%`,
            backgroundColor: pillarColors[i],
            tooltipData: [
                {
                    title: pillarName?.name,
                    number: formatNumber(e.pillarValue, userLocale, '1.0-2'),
                },
            ],
        });
    });

    return pillarsData;
}

export function getPeriodTypes(
    periodTypes: PeriodTypeShallow[],
): CmpsScorecardSubHeaderData[] {
    const periodTypesData: CmpsScorecardSubHeaderData[] = [];
    periodTypes = sort(periodTypes, 'periodTypeOrder');
    periodTypes.forEach((e) => {
        periodTypesData.push({
            value: e.periodTypeName,
            id: e.periodTypeId,
        });
    });
    return periodTypesData;
}

export function getPillarColorsArr(length: number): string[] {
    const colors = [
        '#3957d1',
        '#5e74cf',
        '#7486cf',
        '#8a97ce',
        '#9fa8cd',
        '#b6bbcd',
    ];
    return colors
        .concat(...Array.from({ length: 3 }, () => colors))
        .slice(0, length);
}

export function sort<T>(arr: T[], key: string): T[] {
    const ret = arr != null ? [...arr] : [];
    ret.sort((a, b) => a[key] - b[key]);
    return ret;
}
